// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { RefObject, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import FullCalendar, {
  EventClickArg,
  DayCellContentArg,
  DayHeaderContentArg,
  SlotLabelContentArg,
  NowIndicatorContentArg,
  EventDropArg,
  DateSelectArg,
  DateInput,
  EventSourceInput,
  OverlapFunc,
  ConstraintInput,
  CssDimValue,
  DurationInput,
  Duration,
  CustomContentGenerator,
  EventContentArg,
  AllowFunc,
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, {
  DateClickArg,
  EventResizeDoneArg,
} from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon';
import jaLocale from '@fullcalendar/core/locales/ja';

import {
  spacing,
  fontSize,
  fontWeight,
  borderRadius,
  mediaMaxWithLayoutTheme,
} from 'presentations/views/styles';
import {
  primary,
  gray01,
  gray03,
  gray04,
  white,
  black,
  whiteFix,
  gray05,
  blueGrayPale,
} from 'presentations/views/styles/color';
import { getDateTime } from 'infras/datetime';
import { defaultDisplayWeekDay } from 'domains/models/event_type/displayWeekDay';

interface CalendarStyleProps {
  withoutOutline?: boolean;
  withoutContainerBorder?: boolean;
}

export interface Props {
  locale: string;
  timeZone: string;
  fullCalendarRef?: RefObject<FullCalendar>;
  initialView:
    | 'dayGridMonth'
    | 'timeGridWeek'
    | 'timeGridDay'
    | 'timeGridThreeDay';
  displayEventTime?: boolean;
  eventClick?: ((arg: EventClickArg) => void) | undefined;
  initialDate?: DateInput | undefined;
  eventSources?: EventSourceInput[] | undefined;
  eventContent?: CustomContentGenerator<EventContentArg>;
  eventOrderWithoutId?: boolean;
  eventOrder?: any[];
  expandRows?: boolean | undefined;
  height?: CssDimValue | undefined;
  selectable?: boolean | undefined;
  dateClick?: ((arg: DateClickArg) => void) | undefined;
  select?: ((arg: DateSelectArg) => void) | undefined;
  eventDragStart?: (() => void) | undefined;
  eventDragStop?: (() => void) | undefined;
  eventDrop?: ((arg: EventDropArg) => void) | undefined;
  eventResize?: ((arg: EventResizeDoneArg) => void) | undefined;
  selectOverlap?: boolean | OverlapFunc | undefined;
  eventOverlap?: boolean | OverlapFunc | undefined;
  selectConstraint?: ConstraintInput | undefined;
  eventConstraint?: ConstraintInput | undefined;
  selectAllow?: AllowFunc | undefined;
  slotEventOverlap?: boolean | undefined;
  slotMinTime?: DurationInput | Duration | null | undefined;
  slotMaxTime?: DurationInput | Duration | null | undefined;
  dayHeaderContent?: CustomContentGenerator<DayHeaderContentArg>;
  eventMinHeight?: number | undefined;
  styleProps?: CalendarStyleProps;
  dayHeaders?: boolean;
  allDaySlot?: boolean | undefined;
  startWeekDay?: number;
  displayWeekdays?: number[];
  selectLongPressDelay?: number;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const hiddenWeekDays = useMemo(() => {
    if (props.displayWeekdays && props.displayWeekdays.length > 0) {
      const defaultDisplayWeekdays = defaultDisplayWeekDay
        .split(',')
        .map((w) => (Number(w) + (props.startWeekDay ?? 0)) % 7);
      const displayWeekdays = props.displayWeekdays;
      return defaultDisplayWeekdays.filter((w) => !displayWeekdays.includes(w));
    }

    return [];
  }, [props.displayWeekdays, props.startWeekDay]);

  const dayCellContent = (args: DayCellContentArg) => {
    const date = args.date.getDate();
    if (args.view.type === 'dayGridMonth') {
      return (
        <>
          {date === 1 && (
            <DayCellContentMonth>
              {DateTime.fromJSDate(args.date).toFormat(
                t('Day Header Month View'),
              )}
            </DayCellContentMonth>
          )}
          <DayCellContentDate>{date}</DayCellContentDate>
        </>
      );
    } else {
      return <></>;
    }
  };

  const dayHeaderContent = (args: DayHeaderContentArg) => {
    if (args.view.type === 'dayGridMonth') {
      return (
        <WeekDayHeaderWeekday>
          {DateTime.fromJSDate(args.date, { locale: props.locale })
            .toUTC()
            .toFormat('EEE')}
        </WeekDayHeaderWeekday>
      );
    } else if (args.view.type === 'timeGridWeek') {
      return (
        <>
          <WeekDayHeaderDate today={args.isToday}>
            {args.date.getDate()}
          </WeekDayHeaderDate>
          <WeekDayHeaderWeekday>
            {DateTime.fromJSDate(args.date, { locale: props.locale }).toFormat(
              'EEE',
            )}
          </WeekDayHeaderWeekday>
        </>
      );
    } else {
      return args.text;
    }
  };

  const slotLabelContent = (args: SlotLabelContentArg) => {
    if (
      args.view.type === 'timeGridWeek' ||
      args.view.type === 'timeGridDay' ||
      args.view.type === 'timeGridThreeDay'
    ) {
      return args.date.getHours() === 0
        ? ''
        : DateTime.fromJSDate(args.date).toFormat('H:mm');
    } else {
      return args.text;
    }
  };

  const nowIndicatorContent = (args: NowIndicatorContentArg) => {
    if (args.view.type === 'timeGridWeek' && args.isAxis) {
      return DateTime.fromJSDate(args.date).toFormat('H:mm');
    } else {
      return '';
    }
  };

  return (
    <StyledFullCalendar
      styleProps={props.styleProps}
      slotEventOverlap={props.slotEventOverlap}
    >
      <FullCalendar
        // 全体設定
        ref={props.fullCalendarRef}
        plugins={[
          timeGridPlugin,
          dayGridPlugin,
          interactionPlugin,
          luxonPlugin,
        ]}
        hiddenDays={hiddenWeekDays}
        allDayText={t('all-day')}
        timeZone={props.timeZone}
        locales={[jaLocale]}
        locale={props.locale === 'ja' ? 'ja' : 'en'} // jaなら日本語、それ以外は英語にフォールバック
        headerToolbar={false}
        firstDay={props.startWeekDay}
        initialView={props.initialView}
        dayMaxEvents={true}
        nowIndicator={true}
        displayEventTime={props.displayEventTime}
        eventClick={props.eventClick}
        initialDate={props.initialDate}
        scrollTime={getDateTime(null, props.locale).toFormat('HH:mm')}
        scrollTimeReset={false}
        eventSources={props.eventSources}
        eventContent={props.eventContent}
        eventOrder={
          props.eventOrder
            ? props.eventOrder
            : props.eventOrderWithoutId
            ? ['start', '-duration', 'allDay', 'title']
            : ['-id', 'start', '-duration', 'allDay', 'title']
        }
        expandRows={props.expandRows}
        stickyHeaderDates
        height={props.height || '100%'}
        views={{
          timeGridThreeDay: {
            type: 'timeGrid',
            duration: { days: 3 },
          },
        }}
        // 選択
        selectable={props.selectable}
        selectMinDistance={5}
        selectLongPressDelay={props.selectLongPressDelay}
        dateClick={props.dateClick}
        select={props.select}
        eventDragStart={props.eventDragStart}
        eventDragStop={props.eventDragStop}
        eventDrop={props.eventDrop}
        eventResize={props.eventResize}
        snapDuration={{ minutes: 15 }}
        selectOverlap={props.selectOverlap}
        eventOverlap={props.eventOverlap}
        selectConstraint={props.selectConstraint}
        selectAllow={props.selectAllow}
        eventConstraint={props.eventConstraint}
        // 月表示
        dayCellContent={dayCellContent}
        fixedWeekCount={false}
        dayPopoverFormat={t('More Popover Date')}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        slotMinTime={props.slotMinTime || '00:00:00'}
        slotMaxTime={props.slotMaxTime || '24:00:00'}
        // 週表示
        slotEventOverlap={
          typeof props.slotEventOverlap === 'boolean'
            ? props.slotEventOverlap
            : true
        }
        dayHeaderContent={props.dayHeaderContent ?? dayHeaderContent}
        slotLabelContent={slotLabelContent}
        nowIndicatorContent={nowIndicatorContent}
        eventMinHeight={props.eventMinHeight}
        allDaySlot={props.allDaySlot}
      />
    </StyledFullCalendar>
  );
}

export const StyledFullCalendar = styled.div<{
  styleProps?: CalendarStyleProps;
  slotEventOverlap?: boolean;
}>`
  height: 100%;
  overflow: hidden;
  border-radius: ${(props) =>
    !props.styleProps?.withoutContainerBorder && borderRadius(6)};
  border: ${(props) =>
    !props.styleProps?.withoutContainerBorder && `border: 1px solid ${gray04}`};
  border-color: ${(props) =>
    !props.styleProps?.withoutContainerBorder && gray04};

  // 全体
  & > .fc .fc-scrollgrid {
    background-color: ${white};
    border: 0;
    ${({ styleProps }) =>
      styleProps?.withoutOutline
        ? css`
            border-width: 0;
          `
        : css`
            border-right-width: 1px;
            border-bottom-width: 1px;
          `}

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      border-bottom: 0;
      border-radius: 0 !important;
    }
  }
  & > .fc-theme-standard td {
    border-color: ${gray05};
    border-right-width: 0;
    border-bottom-width: 0;
    background-color: transparent;
  }
  & > .fc-theme-standard th {
    border-color: ${gray05};
    border-right-width: 0;
    border-left-width: 0;
  }
  & > .fc .fc-timegrid-axis {
    border-right-width: 1px;
  }
  & > .fc .fc-event-main {
    overflow: hidden;
    padding: 1px 4px 1px 6px;
  }
  & > .fc .fc-event-main .capacity {
    color: ${gray01};
    font-size: ${fontSize(10)};
    font-weight: ${fontWeight('normal')};
  }
  & > .fc .fc-event-main .select-capacity {
    color: ${white};
    font-size: ${fontSize(10)};
    font-weight: ${fontWeight('normal')};
  }

  // ヘッダ
  & > .fc .fc-col-header-cell-cushion {
    color: ${gray01};
    font-size: ${fontSize(12)};
    padding: ${spacing(16)} 0;

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }

  // 日セル
  & > .fc .fc-daygrid .fc-daygrid-day.fc-day-today {
    background-color: initial;
  }
  & > .fc .fc-daygrid .fc-daygrid-day:hover {
    background-color: ${blueGrayPale};
  }
  & > .fc .fc-daygrid .fc-daygrid-day-number {
    display: flex;
    width: 100%;
    padding: ${spacing(4)} ${spacing(8)} ${spacing(4)} ${spacing(8)};
    justify-content: space-between;
    color: ${gray01};
    font-size: ${fontSize(14)};
    font-weight: ${fontWeight('bold')};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      padding: 0 ${spacing(6)} 1px ${spacing(6)};
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-daygrid .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    color: ${primary};
  }

  // イベント(月表示)
  & > .fc .fc-daygrid-event {
    cursor: pointer;
  }
  & > .fc .fc-daygrid-dot-event {
    overflow: hidden;
    margin: 0 ${spacing(8)} 0 6px;
    padding: 0;
    font-size: ${fontSize(11)};
    font-weight: ${fontWeight('bold')};
    color: ${black};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      margin: 0 ${spacing(12)} 0 ${spacing(3)};
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-daygrid-dot-event:hover {
    background-color: initial;
  }
  & > .fc .fc-daygrid-event-dot {
    display: inline-block;
    margin: auto ${spacing(4)} auto 0;
    border-width: 3px;

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      margin: ${spacing(4)} ${spacing(3)};
      border-width: 2px;
    }
  }
  & > .fc .fc-daygrid-block-event {
    margin: 0 4px 2px;
    font-size: ${fontSize(11)};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-daygrid-event .fc-event-time {
    margin-right: ${spacing(4)};
    color: ${gray01};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      display: none;
    }
  }
  & > .fc .fc-daygrid-event .fc-event-title-container {
    display: flex;
    align-items: center;
  }
  & > .fc .fc-daygrid-dot-event .fc-event-title {
    color: ${gray01};
    /* font-weight: initial; */
  }
  & > .fc .fc-daygrid-block-event.allday-event .fc-event-title {
    color: ${white};
  }
  & > .fc .fc-daygrid-block-event.allday-event.light-color .fc-event-title {
    color: ${gray01};
  }
  & > .fc .fc-daygrid-day-bottom {
    margin: 0 0 ${spacing(8)} ${spacing(8)};
    font-size: ${fontSize(11)};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      margin: 0 0 ${spacing(4)} ${spacing(3)};
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-daygrid-more-link {
    color: ${gray01};
  }
  & > .fc .fc-daygrid-block-event .fc-event-title.month-view {
    margin-left: 0;
  }

  // 他○件のポップオーバー
  & > .fc .fc-more-popover {
    box-shadow: 0px 10px 30px rgba(101, 101, 101, 0.15);
    border: none;
    border-radius: ${borderRadius(12)};
  }
  & > .fc .fc-more-popover .fc-popover-body {
    max-width: 200px;
    min-width: 200px;
    padding: 0 ${spacing(16)} ${spacing(16)} ${spacing(16)};
  }
  & > .fc .fc-more-popover .fc-popover-header {
    margin: ${spacing(16)} ${spacing(16)} ${spacing(8)} ${spacing(16)};
    background: none;
  }
  & > .fc .fc-more-popover .fc-popover-title {
    font-size: ${fontSize(12)};
    line-height: 160%;
    font-weight: ${fontWeight('bold')};
    color: ${gray01};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-more-popover-misc {
    display: none;
  }

  // -----
  // 週表示
  // -----

  // 終日(週表示)
  & > .fc .fc-timegrid-slot  {
    height: 2.0em;
    ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
      height: 2.6em;
    }
  }

  & > .fc .fc-timegrid .fc-col-header-cell-cushion {
    padding: ${spacing(24)} 0;

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      padding: ${spacing(12)} 0;
    }
  }
  & > .fc .fc-timegrid-axis-frame {
    font-size: ${fontSize(12)};
    font-weight: ${fontWeight('bold')};
    color: ${gray03};
    text-align: center;

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-daygrid-day.fc-day-today,
  .fc .fc-timegrid-col.fc-day-today {
    background-color: initial;
  }
  & > .fc .fc-timegrid .fc-daygrid-event {
    margin: 0 0 1px 0;
  }
  & > .fc .fc-timegrid .fc-daygrid-day-top {
    display: none;
  }
  & > .fc .fc-daygrid-body-natural .fc-daygrid-day-events {
    margin-bottom: 0;
  }
  & > .fc .fc-timegrid-axis-cushion {
    width: 100%;
    text-align: right;
  }
  & > .fc .fc-timegrid .fc-daygrid-event .fc-event-title {
    /* padding: ${spacing(4)} ${spacing(8)}; */

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      /* padding: ${spacing(3)} ${spacing(4)}; */
    }
  }
  // 右側のスロット表示
  & > .fc .fc-timegrid-slot-minor {
    border-top: none;
  }
  & > .fc .fc-timegrid-slot-label {
    position: relative;
    top: -10px;
    border-top: none;
    font-size: ${fontSize(12)};
    font-weight: ${fontWeight('bold')};
    color: ${gray03};

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-timegrid-now-indicator-container {
    text-align: right;
    overflow: visible;
  }
  // 現在
  & > .fc .fc-timegrid-now-indicator-arrow {
    padding: 0 ${spacing(4)};
    border: none;
    font-size: ${fontSize(12)};
    font-weight: ${fontWeight('bold')};
    color: ${primary};
    width: 100%;

    ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
      font-size: ${fontSize(10)};
    }
  }
  & > .fc .fc-timegrid-now-indicator-line {
    border-color: ${primary};
    border-width: 2px 0 0;
    &::before {
      content: '';
      display: block;
      position: relative;
      top: -5px;
      left: -5px;
      width: 8px;
      height: 8px;
      border-radius: 4px;
      background-color: ${primary};
    }
  }
  // イベント
  & > .fc .fc-timegrid-col-events {
    margin: 0;
    .fc-timegrid-event-harness-inset {
      overflow: hidden;

    }
  }
  & > .fc .fc-timegrid .fc-event {
    cursor: pointer;
  }
  & > .fc .fc-timegrid .fc-event-title {
    white-space: wrap;
    line-height: 1.2;
    color: ${black};
    font-weight: ${fontWeight('bold')};
    margin: 1px 0;
    ${(props: any) => css`
      opacity: ${props.theme.appearance === 'dark' ? 0.7 : 0.8};
    `}
    cursor: pointer;
  }
  & > .fc .fc-v-event {
    border: 0;
  }
  & > .fc .fc-h-event {
    border: 0;
  }
  & > .fc .fc-timegrid .fc-timegrid-event {
    margin-right: ${({ slotEventOverlap }) =>
      slotEventOverlap === false ? '2px' : spacing(6)};

    ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
      margin-right: 2px;
    }
    box-shadow: 0px 0px 0px 1px ${white} !important;
  }
  &
    > .fc
    .fc-timegrid
    .fc-timegrid-event.short-event
    .fc-event-title-container {
    display: flex;
    align-items: center;
  }
  & > .fc .fc-timegrid .fc-timegrid-event.short-event .fc-event-title {
    padding-top: 0;
    padding-bottom: 0;
    line-height: 1em;
  }
  // 投票
  & > .fc .fc-timegrid .fc-timegrid-event.reserved-event {
    border: 2px dotted ${primary};
  }
  & > .fc .fc-timegrid .fc-timegrid-event.reserved-event .fc-event-title {
    color: ${primary};
    opacity: 1;
  }
  & > .fc .fc-timegrid .fc-timegrid-event.reserved-event .fc-event-main div {
    /* height: 100%; */
  }
  & > .fc .fc-timegrid .fc-timegrid-event.select-reserved-event .fc-event-title {
    color: ${white};
    opacity: 1;
  }
  & > .fc .fc-timegrid .fc-timegrid-event.select-reserved-event .fc-event-main div {
    /* height: 100%; */
  }
  & > .fc .fc-timegrid .fc-timegrid-event.block-reserved-event .fc-event-title {
    color: ${gray01};
    opacity: 1;
  }
  & > .fc .fc-timegrid .fc-timegrid-event.block-reserved-event .fc-event-main div {
    /* height: 100%; */
  }

  & > .fc .fc-timegrid .fc-timegrid-event .fc-event-title {
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize(11)};
    line-height: 1.1;
    ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
      line-height: 1;
      letter-spacing: 0.01px;
    }
  }
  & > .fc .fc-timegrid .fc-daygrid-event {
    margin-right: ${spacing(4)};
    /* ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
      margin-right: ${spacing(4)};
    } */
  }
  & > .fc .fc-timegrid .fc-daygrid-event .fc-event-title {
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize(11)};
  }
  & > .fc .fc-daygrid-block-event .fc-event-title {
    padding: 0;
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize(11)};
    color: ${gray01};
  }

  // 選択
  & > .fc .fc-highlight {
    padding: 2px;
    background-color: ${white};
    border: none;
    border: 2px dotted ${primary};
    border-radius: ${borderRadius(3)};
    /* background-image: repeating-linear-gradient(0deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(90deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(180deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(270deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px);
    background-size: 2px calc(100% + 12px), calc(100% + 12px) 2px, 2px calc(100% + 12px) , calc(100% + 12px) 2px;
    background-position: 0 0, 0 0, 100% 0, 0 100%;
    background-repeat: no-repeat; */
    /* animation: borderAnimation 2.0s infinite linear; */

    margin-right: ${spacing(6)};
    ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
      margin-right: ${spacing(12)};
    }
  }
  & > .fc .fc-timegrid-event.creating-event,
  & > .fc .fc-daygrid-event.creating-event {
    padding: 0 2px;
    border: none;
    border: 2px dotted ${primary};
    border-radius: ${borderRadius(3)};
    /* background-image: repeating-linear-gradient(0deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(90deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(180deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px), repeating-linear-gradient(270deg, ${primary}, ${primary} 2px, transparent 2px, transparent 4px, ${primary} 4px);
    background-size: 2px calc(100% + 12px), calc(100% + 12px) 2px, 2px calc(100% + 12px) , calc(100% + 12px) 2px;
    background-position: 0 0, 0 0, 100% 0, 0 100%;
    background-repeat: no-repeat; */
    /* animation: borderAnimation 2.0s infinite linear; */
  }
  @keyframes borderAnimation {
    from { background-position: 0 0, -12px 0, 100% -12px, 0 100%; }
    to { background-position: 0 -12px, 0 0, 100% 0, -12px 100%; }
  }
  & > .fc .fc-daygrid-dot-event.creating-event {
    padding: 0px ${spacing(4)};
  }
  & > .fc .fc-timegrid-event.creating-event {
    background-color: ${white} !important;
  }
  & > .fc .fc-timegrid-event.creating-event .fc-event-title,
  & > .fc .fc-daygrid-event.creating-event .fc-event-title {
    color: ${primary};
    word-break: break-all;
    white-space: pre;
  }
  & > .fc .fc-daygrid-event.creating-event .fc-daygrid-event-dot,
  & > .fc .fc-daygrid-event.creating-event .fc-event-time {
    display: none;
  }
`;
const DayCellContentDate = styled.div`
  flex-grow: 1;
  text-align: right;
`;
const DayCellContentMonth = styled.div`
  color: ${gray01};
  font-weight: ${fontWeight('bold')};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    display: none;
  }
`;
const WeekDayHeaderDate = styled.div<{ today: boolean }>`
  width: 40px;
  height: 40px;
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  line-height: 40px;
  vertical-align: middle;
  border-radius: ${({ today }) => (today ? '20px' : 'initial')};
  background-color: ${({ today }) => (today ? primary : 'initial')};
  color: ${({ today }) => (today ? whiteFix : 'inherit')};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
    line-height: 20px;
    border-radius: ${({ today }) => (today ? '10px' : 'initial')};
  }
`;
const WeekDayHeaderWeekday = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    font-size: ${fontSize(10)};
  }
`;
